import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { Page } from "v2/components/Page";
import { PanelStatus, StatusBadge } from "v2/components/StatusBadge";
import { IActivityHealth, IWorkspaceHealth } from "./contracts/types";
import { getCnpj } from "./operational-panel.service";
import { NEVER_SUCCEEDED, worstDaysSinceLastSuccess } from "./aggregate";
import { formatFederalTaxId, formatShortDate, formatWorstDelay } from "./format";
import { formatSaoPauloDateTime } from "./dates";

/** Ordem canônica do ciclo de movimentação (spec §2). Atividades fora desta lista entram depois, em ordem alfabética. */
const ACTIVITY_ORDER = [
  "Preparar massa",
  "Regras de contrato",
  "Identifica movimentações",
  "Sincronização API",
  "Retorno da operadora",
  "Governança",
  "Carga na operadora (QBem)",
  "Exportação",
];

/** Legenda dos ícones da tabela (spec F0.3, wireframe nível 2). */
/**
 * `AGUARDANDO` vai por último, junto da nota de ação de usuário, porque os
 * dois são a mesma categoria: não são veredito de saúde. Ele **precisa**
 * estar aqui — as células da matriz são só ícone, sem texto, então a legenda
 * é o único lugar onde o operador descobre o que a ampulheta significa.
 */
const LEGEND_STATUSES: PanelStatus[] = ["SUCCESS", "FAILED", "PENDING", "PAROU", "NUNCA_RODOU", "SEM_DADO", "AGUARDANDO"];

function sortActivityNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const indexA = ACTIVITY_ORDER.indexOf(a);
    const indexB = ACTIVITY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export function CnpjPage() {
  const { federalTaxId } = useParams<{ federalTaxId: string }>();
  const history = useHistory();

  const [operatorFilter, setOperatorFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["operational-panel", "cnpj", federalTaxId],
    queryFn: () => getCnpj(federalTaxId),
    refetchInterval: 5000,
  });

  /**
   * T5 (plano v2, 2026-08-11): uma coluna só existe se a atividade já rodou
   * pelo menos uma vez em algum workspace deste CNPJ.
   *
   * Medido em produção: 583 das 839 atividades configuradas nos contratos
   * ATIVO (69,5%) nunca executaram — na ENESA são 15 colunas de 27. Renderizar
   * todas enche a tabela de colunas inteiramente vazias, que é ruído puro: uma
   * coluna sem nenhuma execução na história não tem o que informar.
   *
   * Se elas *deveriam* existir como `workspace_activity` é outra questão, de
   * produto, deliberadamente fora deste plano.
   */
  const activityColumns = useMemo(() => {
    if (!data) return [];
    const names = new Set<string>();
    data.workspaces.forEach((workspace) =>
      workspace.activities.forEach((activity) => {
        if (activity.lastExecutionAt) names.add(activity.activityName);
      })
    );
    return sortActivityNames(Array.from(names));
  }, [data]);

  const hiddenColumnCount = useMemo(() => {
    if (!data) return 0;
    const all = new Set<string>();
    data.workspaces.forEach((workspace) => workspace.activities.forEach((activity) => all.add(activity.activityName)));
    return all.size - activityColumns.length;
  }, [data, activityColumns]);

  const operatorOptions = useMemo(() => Array.from(new Set(data?.workspaces.map((ws) => ws.operatorName) ?? [])), [data]);
  const productOptions = useMemo(() => Array.from(new Set(data?.workspaces.map((ws) => ws.productName) ?? [])), [data]);

  const filteredWorkspaces = useMemo(() => {
    if (!data) return [];
    return data.workspaces.filter((workspace) => {
      if (operatorFilter && workspace.operatorName !== operatorFilter) return false;
      if (productFilter && workspace.productName !== productFilter) return false;
      return true;
    });
  }, [data, operatorFilter, productFilter]);

  function goToWorkspace(workspace: IWorkspaceHealth, activity: IActivityHealth) {
    history.push(`/v2/painel-operacional/workspace/${workspace.workspaceId}/atividade/${activity.activityId}`);
  }

  return (
    <Page title={data ? data.buyerName : "CNPJ"}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <IconButton size="small" onClick={() => history.push("/v2/painel-operacional")}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h6">{data?.buyerName ?? "—"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data ? formatFederalTaxId(data.federalTaxId) : ""} · {data?.sellerName}
            </Typography>
          </Box>
        </Stack>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {isError && <Alert severity="warning">Não foi possível confirmar o estado atual. Dado pode estar desatualizado.</Alert>}

        {!isLoading && !isError && !data && <Alert severity="info">CNPJ não encontrado na janela observada.</Alert>}

        {data && (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                size="small"
                label="Operadora"
                value={operatorFilter}
                onChange={(event) => setOperatorFilter(event.target.value)}
                sx={{ minWidth: 200 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Todas</MenuItem>
                {operatorOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Produto"
                value={productFilter}
                onChange={(event) => setProductFilter(event.target.value)}
                sx={{ minWidth: 200 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Todos</MenuItem>
                {productOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  {activityColumns.map((name) => (
                    <TableCell key={name} align="center">
                      {name}
                    </TableCell>
                  ))}
                  <TableCell align="right">Desde último sucesso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWorkspaces.map((workspace) => {
                  const worstDays = worstDaysSinceLastSuccess(workspace.activities);

                  return (
                    <TableRow key={workspace.workspaceId} hover>
                      <TableCell>
                        {workspace.operatorName} · {workspace.productName}
                        {workspace.policyContractNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Contrato {workspace.policyContractNumber}
                          </Typography>
                        )}
                      </TableCell>
                      {activityColumns.map((name) => {
                        const activity = workspace.activities.find((item) => item.activityName === name);
                        return (
                          <TableCell key={name} align="center" sx={{ cursor: activity ? "pointer" : "default" }}>
                            {activity ? (
                              <Box onClick={() => goToWorkspace(workspace, activity)}>
                                <ActivityCell activity={activity} />
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        {formatWorstDelay(worstDays) === null ? (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            component="span"
                            color={worstDays === NEVER_SUCCEEDED ? "error.main" : "text.primary"}
                          >
                            {formatWorstDelay(worstDays)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 2, rowGap: 1 }}>
              {LEGEND_STATUSES.map((status) => (
                <StatusBadge key={status} status={status} />
              ))}
              {/* T5: a célula de ação de usuário não é um estado de saúde — a legenda precisa dizer isso. */}
              <Tooltip title="Ação de usuário (ON_DEMAND): a célula mostra a data e o resultado do último acionamento, não um estado de saúde. Não entra no veredito do workspace.">
                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "underline dotted" }}>
                  data · resultado = ação de usuário
                </Typography>
              </Tooltip>
            </Stack>

            {hiddenColumnCount > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {hiddenColumnCount} atividade{hiddenColumnCount > 1 ? "s" : ""} configurada
                {hiddenColumnCount > 1 ? "s" : ""} sem nenhuma execução na história —{" "}
                {hiddenColumnCount > 1 ? "omitidas" : "omitida"} da tabela.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Page>
  );
}

/**
 * T5 (plano v2, 2026-08-11) — ação de usuário perde o semáforo e ganha
 * "último uso".
 *
 * Uma atividade `ON_DEMAND` só roda quando alguém clica. Dar a ela um badge de
 * saúde é responder a pergunta errada: não interessa se "está saudável", e sim
 * quando foi acionada pela última vez e com que resultado. Um X vermelho numa
 * exportação que ninguém pediu há três meses não é incidente — é o registro de
 * que ninguém pediu.
 *
 * Continua clicável para o histórico, e continua exibida: o T1 tirou o
 * ON_DEMAND do veredito, não da tela.
 */
function ActivityCell({ activity }: { activity: IActivityHealth }) {
  if (activity.cadence !== "ON_DEMAND") {
    return <StatusBadge status={activity.status} label=" " />;
  }

  if (!activity.lastExecutionAt) {
    return (
      <Typography variant="caption" color="text.disabled">
        nunca usada
      </Typography>
    );
  }

  return (
    <Tooltip title={`Ação de usuário — último acionamento em ${formatSaoPauloDateTime(activity.lastExecutionAt)}`}>
      <Stack alignItems="center" spacing={0.25}>
        <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatShortDate(activity.lastExecutionAt)}
        </Typography>
        <Typography variant="caption" color={activity.status === "FAILED" ? "error.main" : "text.secondary"}>
          {activity.status === "FAILED" ? "falhou" : "ok"}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
